import { Link } from "react-router";
import { cn } from "@/lib/cn";
import { Alerta, Badge, Button, DataList, Icono, MetricCard, Row, Select, Stack } from "@/components";
import type { CuentaDetalle, LlamadoMio, Movimiento } from "@/lib/api";
import { COLOR_SALDO, ETIQUETA_TIPO_MOVIMIENTO, TONO_SALDO, detalleSaldo, estadoSaldo, linkMovimiento, montoMovimiento, saldoContable, tituloSaldo, formatSaldo } from "@/lib/cuentas";
import { formatFecha, formatMoneda } from "@/lib/format";
import { parseMonto } from "@/lib/gastos";
import { formatParticipacion } from "@/lib/participacion";
import { useMovimientosCuenta } from "./useMovimientosCuenta";

const claseLink = "text-primario underline-offset-2 hover:underline";

const columnasMovimientos = [
  { clave: "fecha", titulo: "Fecha", render: (m: Movimiento) => formatFecha(m.fecha) },
  {
    clave: "concepto",
    titulo: "Concepto",
    render: (m: Movimiento) => {
      const link = linkMovimiento(m);
      return (
        <Row espacio={2}>
          <Badge tono={m.tipo === "aporte" ? "primario" : m.tipo === "adjudicacion" ? "alerta" : "neutro"}>{ETIQUETA_TIPO_MOVIMIENTO[m.tipo]}</Badge>
          {link ? <Link to={link} className={claseLink}>{m.concepto}</Link> : <span>{m.concepto}</span>}
        </Row>
      );
    },
  },
  {
    clave: "parte",
    titulo: "Su parte",
    render: (m: Movimiento) => (m.total_usd !== null && m.porcentaje !== null ? `${formatParticipacion(m.porcentaje)} de ${formatMoneda(m.total_usd, "USD")}` : "—"),
  },
  {
    clave: "monto",
    titulo: "Monto",
    render: (m: Movimiento) => {
      const { texto, estado } = montoMovimiento(m);
      return <span className={cn("tabular-nums", COLOR_SALDO[estado])}>{texto}</span>;
    },
  },
  { clave: "saldo", titulo: "Saldo", render: (m: Movimiento) => saldoContable(m.saldo_usd) },
];

const columnasLlamados = [
  { clave: "numero", titulo: "Previsión", render: (l: LlamadoMio) => <Link to={`/llamados/${l.id}`} className={claseLink}>{`Previsión ${l.numero}`}</Link> },
  { clave: "clase", titulo: "Clase", render: (l: LlamadoMio) => <Badge tono={l.clase === "B" ? "alerta" : "neutro"}>{l.clase}</Badge> },
  { clave: "vence", titulo: "Vence", render: (l: LlamadoMio) => formatFecha(l.vencimiento) },
  { clave: "pedido", titulo: "Pedido", render: (l: LlamadoMio) => formatMoneda(l.pedido_usd, "USD") },
  { clave: "imputado", titulo: "Aportado", render: (l: LlamadoMio) => formatMoneda(l.imputado_usd, "USD") },
  {
    clave: "pendiente",
    titulo: "Pendiente",
    render: (l: LlamadoMio) => (
      <Row espacio={2}>
        <span>{formatMoneda(l.pendiente_usd, "USD")}</span>
        {l.vencida && <Badge tono="peligro">Vencido</Badge>}
      </Row>
    ),
  },
];

/**
 * La cuenta corriente de un fiduciante: sus números, los movimientos con el saldo acumulado (aportes contra su parte
 * de los gastos efectuados) y sus llamados. Es la ficha del admin/contador (Mi cuenta del fiduciante es más simple).
 */
export function DetalleCuenta({ cuenta }: { cuenta: CuentaDetalle }) {
  const hayVencido = parseMonto(cuenta.vencido_usd) !== null;
  return (
    <Stack espacio={6}>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <MetricCard titulo="Le pidieron" valor={formatMoneda(cuenta.pedido_usd, "USD")} detalle="En las previsiones" />
        <MetricCard titulo="Aportó" valor={formatMoneda(cuenta.aportado_usd, "USD")} />
        <MetricCard titulo="Su parte de los gastos" valor={formatMoneda(cuenta.gastos_usd, "USD")} detalle="Gastos ya pagados" />
        <MetricCard titulo={tituloSaldo(cuenta)} tono={TONO_SALDO[estadoSaldo(cuenta.saldo_usd)]} valor={formatSaldo(cuenta.saldo_usd)} detalle={detalleSaldo(cuenta)} />
      </div>
      <Alerta texto={hayVencido ? `Tiene ${formatMoneda(cuenta.vencido_usd, "USD")} vencido en las previsiones.` : null} />
      <Movimientos movimientos={cuenta.movimientos} />
      <DataList etiqueta="Previsiones" columnas={columnasLlamados} filas={cuenta.llamados} claveFila={(l) => `${l.id}-${l.clase}`} vacio="Todavía no hay previsiones de gastos." />
    </Stack>
  );
}

/** Los movimientos con filtros: mes (por defecto el último, con flechas ‹ ›), tipo, y de a 20 con "Cargar más". */
function Movimientos({ movimientos }: { movimientos: Movimiento[] }) {
  const t = useMovimientosCuenta(movimientos);
  return (
    <Stack espacio={3}>
      {t.hayMovimientos && (
        <div className="flex flex-wrap items-end gap-2">
          <Button variante="secundario" tamanio="md" onClick={t.mesAnterior ?? undefined} disabled={!t.mesAnterior} aria-label="Mes anterior">
            <Icono nombre="flecha" className="size-4 rotate-180" />
          </Button>
          <Select etiqueta="Mes" opciones={t.opcionesMes} valor={t.filtros.mes} onCambio={t.cambiarMes} className="min-w-44" />
          <Button variante="secundario" tamanio="md" onClick={t.mesSiguiente ?? undefined} disabled={!t.mesSiguiente} aria-label="Mes siguiente">
            <Icono nombre="flecha" className="size-4" />
          </Button>
          <Select etiqueta="Tipo" opciones={t.opcionesTipo} valor={t.filtros.tipo} onCambio={t.cambiarTipo} className="min-w-48" />
        </div>
      )}
      <DataList
        etiqueta="Movimientos"
        columnas={columnasMovimientos}
        filas={t.filas}
        claveFila={(m) => m.n /* un gasto con dos pagos el mismo día da dos filas iguales: la posición las distingue */}
        vacio={t.hayMovimientos ? "No hay movimientos con estos filtros." : "Todavía no hay aportes ni gastos pagados."}
      />
      {t.textoCargarMas && (
        <div>
          <Button variante="secundario" onClick={t.cargarMas}>{t.textoCargarMas}</Button>
        </div>
      )}
    </Stack>
  );
}
